/**
 * Timeline Export Utilities
 * For creating Premiere Pro/Final Cut Pro compatible XML and EDL timelines
 */

import { logger } from "@/services/loggerService";

/**
 * Convert frames to timecode string
 * @param {number} frames - Frame number
 * @param {number} timebase - Frames per second
 * @returns {string} Timecode string in HH:MM:SS:FF format
 */
const framesToTimecode = (frames, timebase) => {
  const hours = Math.floor(frames / (timebase * 3600));
  const minutes = Math.floor((frames % (timebase * 3600)) / (timebase * 60));
  const seconds = Math.floor((frames % (timebase * 60)) / timebase);
  const frameNum = frames % timebase;
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(frameNum).padStart(2, '0')}`;
};

/**
 * Generate EDL content for Premiere Pro import
 * @param {Array} assets - Array of asset objects
 * @returns {string} EDL content
 */
const generateEDLContent = (assets) => {
  const fps = 24;
  const defaultDurationSeconds = 10;
  // Non-drop frame for 24fps
  
  // EDL header
  let edl = `TITLE: Imported Media\n`;
  edl += `FCM: NON-DROP FRAME\n\n`;
  
  let eventNumber = 1;
  let recordTimecode = 0; // in frames
  
  assets.forEach((asset) => {
    const durationSeconds = asset.duration || defaultDurationSeconds;
    const durationFrames = Math.round(durationSeconds * fps);
    
    // Convert frames to timecode format
    // Source should start at 00:00:00:00 and end at the clip duration
    const sourceIn = "00:00:00:00";
    const sourceOut = framesToTimecode(durationFrames - 1, fps); // End frame is inclusive
    const recordIn = framesToTimecode(recordTimecode, fps);
    const recordOut = framesToTimecode(recordTimecode + durationFrames - 1, fps); // End frame is inclusive
    
    // Reel name (max 8 characters)
    const reelName = asset.name.substring(0, 8).toUpperCase().replace(/[^A-Z0-9]/g, '').padEnd(8, ' ');
    
    // EDL event line format: EVENT# REEL AUD TRANS DUR SRC_IN SRC_OUT REC_IN REC_OUT
    // Use proper spacing for CMX 3600 format
    const eventStr = String(eventNumber).padStart(3, '0');
    const transType = 'C'; // Cut transition
    const tracks = 'V'; // Video only
    
    // Format: "001  REEL001  V     C        00:00:00:00 00:00:10:00 00:00:00:00 00:00:10:00"
    edl += `${eventStr}  ${reelName} ${tracks}     ${transType}        ${sourceIn} ${sourceOut} ${recordIn} ${recordOut}\n`;
    edl += `* FROM CLIP NAME: ${asset.name}\n`;
    
    edl += `\n`;
    
    recordTimecode += durationFrames;
    eventNumber++;
  });
  
  return edl;
};

/**
 * Download EDL timeline with selected assets
 * @param {Array} selectedAssets - Array of selected asset objects
 * @param {Function} showToast - Function to show toast notifications (text, theme)
 */
export const downloadEDLFile = (selectedAssets, showToast) => {
  if (selectedAssets.length === 0) {
    showToast("No assets selected to export", "warning");
    return;
  }

  try {
    const edl = generateEDLContent(selectedAssets);
    const blob = new Blob([edl], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    // Create download link
    const link = document.createElement("a");
    link.href = url;
    link.download = `timeline_${new Date().toISOString().slice(0, 10)}.edl`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);

    // Show success toast
    showToast(
      `EDL timeline downloaded with ${selectedAssets.length} assets`,
      "success"
    );

    logger.info("[XMLUtils] EDL timeline downloaded", {
      assetCount: selectedAssets.length,
      format: "EDL",
      assets: selectedAssets.map((asset) => ({
        id: asset.id,
        name: asset.name,
        duration: asset.duration,
      })),
    });
  } catch (error) {
    logger.error("[XMLUtils] Failed to generate EDL:", {
      error: error.message,
      assetCount: selectedAssets.length,
    });

    showToast("Failed to generate EDL timeline", "danger");
  }
}; 